import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
    { id: "features", label: "Features" },
    { id: "how-it-works", label: "How it works" },
    { id: "about", label: "About" },
    { id: "faqs", label: "FAQs" },
    { id: "contact", label: "Contact" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        if (pathname !== "/") {
            navigate("/");
            setTimeout(() => (id ? scrollToId(id) : scrollToTop()), 90);
        } else if (id) {
            scrollToId(id);
        } else {
            scrollToTop();
        }
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
                scrolled || open
                    ? "border-b border-white/10 bg-[#0A0A0B]/85 backdrop-blur-xl"
                    : "border-b border-transparent"
            }`}
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8 lg:h-[72px] lg:px-12">
                <button
                    data-testid="nav-brand-logo"
                    onClick={() => go(null)}
                    className="font-display text-2xl font-extrabold lowercase tracking-tight text-fynary-lime"
                >
                    fynary.
                </button>

                <div className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="text-sm font-medium text-fynary-muted transition-[color] duration-200 hover:text-white"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-cta-contact"
                        onClick={() => go("contact")}
                        className="rounded-full bg-fynary-lime px-5 py-2.5 text-sm font-semibold text-fynary-bg transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]"
                    >
                        Get in touch
                    </button>
                </div>

                <button
                    data-testid="nav-mobile-menu-button"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-fynary-surface text-white md:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-b border-white/10 bg-[#0A0A0B]/95 backdrop-blur-xl md:hidden"
                    >
                        <div className="flex flex-col gap-1 px-4 pb-6 pt-2">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`nav-link-${l.id}`}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.3 }}
                                    onClick={() => go(l.id)}
                                    className="rounded-xl px-3 py-3 text-left font-display text-xl font-semibold text-white transition-[background-color] duration-200 hover:bg-white/5"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="nav-cta-contact"
                                onClick={() => go("contact")}
                                className="mt-3 rounded-full bg-fynary-lime px-5 py-3 text-center text-sm font-semibold text-fynary-bg"
                            >
                                Get in touch
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
